const CartState = {
  quantities: new Map(),

  init() {
    const cartStateElement = document.getElementById('cart-state');
    if (!cartStateElement) return;

    const items = parseJson(cartStateElement.textContent);
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      if (item?.variant_id) {
        this.quantities.set(String(item.variant_id), item.quantity);
      }
    });
  },

  getQuantity(variantId) {
    return this.quantities.get(String(variantId)) || 0;
  },

  syncFromItems(items = []) {
    this.quantities.clear();
    items.forEach((item) => {
      if (item?.variant_id && !item.properties?._donation_price && !item.properties?._donation_type?.includes('-cart')) {
        this.quantities.set(String(item.variant_id), item.quantity);
      }
    });
  },

  getSectionsToRender() {
    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.getSectionsToRender) {
      sections.push(...cartDrawer.getSectionsToRender().map((section) => section.id));
    } else {
      sections.push('cart-icon-bubble');
    }
    const cartSummary = document.querySelector('cart-summary');
    if (cartSummary?.getSectionsToRender) {
      sections.push(...cartSummary.getSectionsToRender().map((section) => section.id));
    }
    return sections;
  },

  renderSections(parsedState) {
    if (!parsedState?.sections) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.renderContents) {
      cartDrawer.renderContents(parsedState);
    } else {
      const cartIconBubble = document.querySelector('[data-cart-icon-bubble]');
      const sectionHtml = parsedState.sections['cart-icon-bubble'];
      const sectionContent = this.getSectionInnerHTML(sectionHtml, '[data-cart-icon-bubble]');
      if (cartIconBubble && sectionContent) {
        cartIconBubble.innerHTML = sectionContent;
      }
    }

    const cartSummary = document.querySelector('cart-summary');
    if (cartSummary?.renderContents) {
      cartSummary.renderContents(parsedState);
    }
  },

  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element?.innerHTML || null;
  },

  dispatchUpdate() {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { quantities: Object.fromEntries(this.quantities) },
    }));
  },

  applyCartResponse(parsedState) {
    if (!parsedState) return;

    if (Array.isArray(parsedState.items)) {
      this.syncFromItems(parsedState.items);
    } else if (parsedState.variant_id != null && parsedState.quantity != null) {
      this.quantities.set(String(parsedState.variant_id), parsedState.quantity);
    }

    this.dispatchUpdate();
  },

  syncFromCartResponse(parsedState) {
    if (!document.querySelector('product-card-atc')) return;
    this.applyCartResponse(parsedState);
  },

  async addVariant(variantId, quantity = 1) {
    const sections = this.getSectionsToRender();
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    const response = await fetch(`${routes.cart_add_url}`, config);
    const parsedState = await response.json();

    if (parsedState.status === 422) {
      throw new Error(parsedState.description || parsedState.message || 'Unable to add item to cart');
    }

    this.applyCartResponse(parsedState);
    this.renderSections(parsedState);
    return parsedState;
  },

  async updateVariant(variantId, quantity, signal) {
    const body = JSON.stringify({
      id: variantId,
      quantity,
      sections: this.getSectionsToRender(),
      sections_url: window.location.pathname,
    });

    const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body, signal });
    const parsedState = await response.json();

    if (parsedState.status === 422) {
      throw new Error(parsedState.description || parsedState.message || 'Unable to update cart');
    }

    this.applyCartResponse(parsedState);
    this.renderSections(parsedState);
    return parsedState;
  },
};

CartState.init();
window.CartState = CartState;

if (!customElements.get('product-card-atc')) {
  class ProductCardAtc extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.quantityDebounce = null;
      this.desiredQty = null;
      this.commitVersion = 0;
      this.onCartUpdated = this.onCartUpdated.bind(this);
      this.handleAddClick = this.handleAddClick.bind(this);
      this.handleQuantityChange = this.handleQuantityChange.bind(this);
      this.commitQuantity = this.commitQuantity.bind(this);
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addButton = this.querySelector('[data-atc-add]');
      this.quantityInput = this.querySelector('[data-atc-quantity] input');
      this.variantId = this.dataset.variantId;

      this.addButton?.addEventListener('click', this.handleAddClick, { signal });
      this.quantityInput?.addEventListener('change', this.handleQuantityChange, { signal });
      document.addEventListener('cart:updated', this.onCartUpdated, { signal });

      this.syncUI();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.updateController?.abort();
      clearTimeout(this.quantityDebounce);
    }

    setVariant(variantId, available = true) {
      this.variantId = String(variantId);
      this.dataset.variantId = this.variantId;
      if (this.addButton) this.addButton.disabled = !available;
      this.classList.toggle('product-card-atc--sold-out', !available);
      this.syncUI();
    }

    syncUI() {
      const qty = CartState.getQuantity(this.variantId);
      // Don't clobber an optimistic value the user is still editing —
      // a cart:updated from another card can fire while our commit is debounced.
      if (this.desiredQty !== null && this.desiredQty !== qty) {
        this.classList.toggle('product-card-atc--active', this.desiredQty > 0);
        return;
      }
      this.desiredQty = null;
      this.classList.toggle('product-card-atc--active', qty > 0);
      if (this.quantityInput) this.quantityInput.value = qty;
    }

    onCartUpdated() {
      if (!this.busy) this.syncUI();
    }

    // Mirror the configured add-to-cart behavior: open the drawer, or show the toast.
    notifyCartChange(detail) {
      const behavior = (window.cartSettings && window.cartSettings.addToCartBehavior) || 'show_toast';
      if (behavior === 'open_cart_drawer') {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.open();
          return;
        }
      }
      document.dispatchEvent(new CustomEvent('cart:toast', { detail }));
    }

    async handleAddClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.busy) return;
      this.busy = true;

      // Show selector immediately for instant feedback
      this.classList.add('product-card-atc--active');
      if (this.quantityInput) this.quantityInput.value = 1;

      try {
        const prevQty = CartState.getQuantity(this.variantId);
        const parsedState = await CartState.addVariant(this.variantId, 1);
        this.syncUI();
        const addedCount = parsedState.quantity != null
          ? Math.abs(parsedState.quantity - prevQty)
          : 1;
        this.notifyCartChange({
          count: addedCount || 1,
          type: 'add'
        });
      } catch (error) {
        console.error(error);
        this.syncUI(); // revert on failure
      } finally {
        this.busy = false;
      }
    }

    // Debounced: rapid +/- clicks step the input synchronously (see QuantityInput),
    // then a single cart update is sent with the final value once clicks settle.
    handleQuantityChange() {
      const next = parseInt(this.quantityInput.value, 10);
      if (!Number.isNaN(next)) this.desiredQty = next;
      clearTimeout(this.quantityDebounce);
      this.quantityDebounce = setTimeout(this.commitQuantity, 350);
    }

    async commitQuantity() {
      // Wait out any in-flight cart request, then commit the latest value.
      if (this.busy) {
        this.quantityDebounce = setTimeout(this.commitQuantity, 350);
        return;
      }
      const next = parseInt(this.quantityInput.value, 10);
      const current = CartState.getQuantity(this.variantId);
      if (Number.isNaN(next) || next === current) {
        this.desiredQty = null;
        return;
      }
      this.busy = true;
      const version = ++this.commitVersion;
      // Abort any prior in-flight update so its stale response can't mutate CartState.
      this.updateController?.abort();
      this.updateController = new AbortController();
      try {
        await CartState.updateVariant(this.variantId, next, this.updateController.signal);
        if (version !== this.commitVersion) return; // a newer commit superseded us
        // Only clear if the user hasn't queued a newer value while this request was in flight;
        // otherwise syncUI()'s guard preserves the pending input until the queued debounce commits.
        if (this.desiredQty === next) this.desiredQty = null;
        this.syncUI();
        this.notifyCartChange({
          count: Math.abs(next - current),
          type: next > current ? 'add' : 'remove'
        });
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        if (version === this.commitVersion && this.quantityInput) {
          this.desiredQty = null;
          this.quantityInput.value = current;
        }
      } finally {
        if (version === this.commitVersion) this.busy = false;
      }
    }
  }

  window.ProductCardAtc = ProductCardAtc;
  customElements.define('product-card-atc', ProductCardAtc);
}
